import { Box, Button, Grid, Menu, MenuItem, TextField, Skeleton, Theme, Typography, useTheme, Checkbox } from "@mui/material";
import { useSelector } from "react-redux";
import { KPI, useDataQuery, useKpisQuery } from "src/context/api/data/api";
import { RootState } from "src/context/store";
import Iconify from "src/components/iconify";
import BankingWidgetSummary from "./views/banking-widget-summary-toogle";
import { enqueueSnackbar } from "notistack";
import { useLocales } from "src/locales";
import { useEffect, useMemo, useRef, useState } from "react";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";


const METRICS = {
    activations: ["ad_brand_traffic", "cpc", "ad_cost", "paid_ctr", "search_impression_share", "top_impression_percentage", "absolute_top_impression_percentage"],
    base: ["brand_searches", "total_brand_traffic", "organic_brand_traffic", "ad_brand_traffic", "blended_ctr", "blended_cpc", "ad_cost"],
}

export const useKPIToggle = (defaultKPIs: string[]) => {
    const hasInitializedPinned = useRef(false);
    const hasInitializedVisible = useRef(false);
    const { data: kpis, isLoading: isLoadingKPIs, isFetching: isFetchingKPIs } = useKpisQuery();
    const filterStore = useSelector((state: RootState) => state.filters);
    const mode = useSelector((state: RootState) => state.filters.extraOptions?.mode);
    const workspace = useCurrentWorkspace();
    const { currentLang: { value: locale } } = useLocales();
    const { t } = useLocales();
    const [kpiToggle, setKPIToggle] = useState<string[]>(defaultKPIs);
    const kpisKeys = useMemo(() => kpis?.filter((kpi: any) =>
        (mode === "activations" && (kpi.viz === "activation" || kpi.viz === "both")) ||
        (mode !== "activations" && (kpi.viz === "base" || kpi.viz === "both"))
    ).map((kpi) => kpi.id), [kpis, mode]);

    const filtersKPI = (kpi: KPI) => kpisKeys?.includes(kpi.id);
    const stats = useMemo(() => kpis?.filter(filtersKPI).map((kpi) => ({
        ...kpi,
        title: kpi[locale as keyof KPI] as string,
        key: kpi.id,
        color: kpi.color,
        textColor: kpi["color:contrast_text"],
        spanColor: kpi["color:lighter"],
        trendColor: kpi["color:dark"],
        icon: "",
    } as const)) || [], [kpis, kpisKeys, locale]);

    const [pinnedMetrics, setPinnedMetrics] = useState<string[]>(METRICS[mode as keyof typeof METRICS] ?? []);
    const [visibleStats, setVisibleStats] = useState<string[]>(METRICS[mode as keyof typeof METRICS] ?? []);




    useEffect(() => {
        setKPIToggle(METRICS[mode as keyof typeof METRICS].slice(0, 2) ?? []);
        setPinnedMetrics(METRICS[mode as keyof typeof METRICS] ?? []);
        setVisibleStats(METRICS[mode as keyof typeof METRICS] ?? []);
    }, [mode]);

    const { data: { data: [compareStatsData] = [] } = {}, isLoading: isLoadingCompareStats, isFetching: isFetchingCompareStats, error: compareStatsError } = useDataQuery({
        start: filterStore?.compareStart,
        end: filterStore?.compareEnd,
        interval: filterStore?.timestep.toLowerCase(),
        workspace_id: workspace?.id as number,
        search_terms: filterStore?.keywords ?? [],
        devices: filterStore?.devices ?? [],
        countries: filterStore?.countries ?? [],
        platforms: filterStore?.platforms?.map((platform: any) => platform.toUpperCase()) ?? [],
        mode,
        output: "charts",
    }, {
        skip: !workspace?.id || !filterStore?.compareStart || !filterStore?.compareEnd || isLoadingKPIs,
    });

    const { data: { data: [statsData] = [] } = {}, isLoading: isLoadingStats, isFetching: isFetchingStats, error: statsError } = useDataQuery({
        start: filterStore?.start,
        end: filterStore?.end,
        interval: filterStore?.timestep.toLowerCase(),
        workspace_id: workspace?.id as number,
        search_terms: filterStore?.keywords ?? [],
        devices: filterStore?.devices ?? [],
        countries: filterStore?.countries ?? [],
        platforms: filterStore?.platforms?.map((platform: any) => platform.toUpperCase()) ?? [],
        mode,
        output: "charts",
        extra_options: filterStore?.extraOptions,
    }, {
        skip: !workspace?.id || !filterStore?.start || !filterStore?.end || isLoadingKPIs,
    });

    const handleToggleKPI = (key: string) => {
        setKPIToggle((prevState: string[] = []) => {
            // Log the number of active pinned metrics
            const pinnedActive = prevState.filter((k) => pinnedMetrics.includes(k));
            // Prevent removing the last active metric
            if (prevState.includes(key)) {
                if (prevState.length <= 1) {
                    enqueueSnackbar(t('dashboard.snackbar.you_cant_deselect_the_last_item'), { variant: 'error' });
                    return prevState;
                }
                if (pinnedMetrics.includes(key) && pinnedActive.length === 1) {
                    enqueueSnackbar(t('dashboard.snackbar.you_cant_deselect_the_last_pinned_item'), { variant: 'error' });
                    return prevState;
                }
                return prevState.filter((k: string) => k !== key);
            } else {
                // Activate the metric
                // @ts-ignore
                if (prevState.length >= 6 && !window.crossbrandNoLimit && !pinnedMetrics.includes(key)) {
                    enqueueSnackbar(t('dashboard.snackbar.max_metrics_limit_reached'), { variant: 'error' });
                    return prevState;
                }
                return [...prevState, key];
            }
        });
    };

    // Ensure at least one pinned metric is always visible
    useEffect(() => {
        const pinnedVisible = visibleStats.filter((k: string) => pinnedMetrics.includes(k));
        if (pinnedVisible.length === 0 && hasInitializedVisible.current) {
            setVisibleStats((prev: string[]) => [pinnedMetrics[0], ...prev]);
        }
    }, [visibleStats, pinnedMetrics]);

    // Ensure at least one metric is always active
    useEffect(() => {
        if (kpiToggle.length === 0 && hasInitializedPinned.current) {
            setKPIToggle((prev: string[]) => [visibleStats[0] || pinnedMetrics[0]]);
        }
    }, [kpiToggle, visibleStats, pinnedMetrics]);


    return {
        statsData,
        compareStatsData,
        stats,
        visibleStats,
        pinnedMetrics,
        handleToggleKPI,
        setVisibleStats,
        setKPIToggle,
        isLoadingKPIs: isLoadingKPIs || isFetchingKPIs,
        isLoadingStats: isLoadingStats || isFetchingStats,
        isLoadingCompareStats: isLoadingCompareStats || isFetchingCompareStats,
        statsError,
        kpiToggle,
    }
}


type Props = ReturnType<typeof useKPIToggle>;


export default function KpiToggle({ statsData, stats, visibleStats, pinnedMetrics, handleToggleKPI, isLoadingKPIs, kpiToggle, statsError, setVisibleStats, setKPIToggle, compareStatsData, isLoadingStats, isLoadingCompareStats }: Props) {
    const filterStore = useSelector((state: RootState) => state.filters);
    const theme = useTheme();
    const [statsMenuAnchorEl, setStatsMenuAnchorEl] = useState<null | HTMLElement>(null);
    const statsMenuOpen = Boolean(statsMenuAnchorEl);
    const [statsMenuSearch, setStatsMenuSearch] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);
    const { t } = useLocales();

    // Handle opening the stats menu
    const handleStatsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setStatsMenuAnchorEl(event.currentTarget);
    };

    // Handle closing the stats menu
    const handleStatsMenuClose = () => {
        setStatsMenuAnchorEl(null);
        setStatsMenuSearch(''); // Reset search field when menu closes
    };

    const handleRemoveStat = (metricKey: string) => {
        setVisibleStats(prevStats => {
            const isLast = prevStats.length <= 1;
            const isPinned = pinnedMetrics.includes(metricKey);
            const activePinned = prevStats.filter(k => pinnedMetrics.includes(k));
            const isLastPinned = isPinned && activePinned.length === 1;

            if (isLast) {
                enqueueSnackbar(t('dashboard.snackbar.you_cant_deselect_the_last_item'), { variant: 'error' });
                return prevStats;
            }
            if (isLastPinned) {
                enqueueSnackbar(t('dashboard.snackbar.you_cant_deselect_the_last_pinned_item'), { variant: 'error' });
                return prevStats;
            }
            return prevStats.filter(k => k !== metricKey);
        });
        setKPIToggle((prevStats: string[]) => prevStats.filter((k: string) => k !== metricKey));
    };

    const handleAddStat = (metricKey: string) => {
        const hasReachedLimit = kpiToggle.length >= 5;
        const canBypassLimit = window.crossbrandNoLimit || pinnedMetrics.includes(metricKey);

        if (hasReachedLimit && !canBypassLimit) {
            enqueueSnackbar(t('dashboard.snackbar.max_metrics_limit_reached'), { variant: 'error' });
            return;
        }

        const addToList = (prevStats: string[]) => [...prevStats, metricKey];

        setVisibleStats(addToList);
        setKPIToggle(addToList);
    };

    // Ajout de la fonction pour gérer le cycle des 3 états
    const handleMetricCheckboxClick = (statKey: string) => {
        const isInVisible = visibleStats.includes(statKey);
        const isInKpi = kpiToggle.includes(statKey);

        if (!isInVisible) {
            // Ajoute à la topbar ET à la chart
            handleAddStat(statKey);
        } else if (isInVisible && isInKpi) {
            // Retire complètement (ni topbar ni chart)
            handleRemoveStat(statKey);
        } else if (isInVisible && !isInKpi) {
            // Si indéterminé, au clic on active la métrique (ajoute à kpiToggle)
            setKPIToggle((prev: string[]) => prev.includes(statKey) ? prev : [...prev, statKey]);
        }
    };

    return (
        <>
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: filterStore?.compare ? 170 : 110,
                    zIndex: -10,
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.darker.lighter : 'white',
                    borderTopLeftRadius: 20,
                    borderTopRightRadius: 20,
                }}
            />
            <Grid container sx={{
                display: 'flex',
                overflowX: 'auto',
                flexWrap: 'nowrap',
                width: 'calc(100% - 40px)', // Soustrait la largeur du bouton +
                '&::-webkit-scrollbar': {
                    height: 6,
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.divider,
                    borderRadius: 3,
                },
            }}>
                {(isLoadingKPIs || isLoadingStats || isLoadingCompareStats) ? Array.from({ length: 8 }).map((_, index) => (
                    <Grid item key={index} xs={6} md={4} lg={2} sx={{
                        flex: { xs: '0 0 50%', sm: '0 0 25%', md: 1 },
                        minWidth: 210,
                    }}>
                        <Skeleton
                            width={'100%'}
                            height={filterStore?.compare ? '170px' : '110px'}
                            style={{
                                borderRadius: 0,
                                borderTopLeftRadius: index === 0 ? 20 : 0,
                            }}
                            animation="wave"
                        >
                            <>
                                <Iconify py="auto" icon="eva:checkmark-circle-2-fill" color="success.main" />
                                <Typography color="success.main">Active</Typography>
                            </>
                        </Skeleton>
                    </Grid>
                )) : (
                    stats?.filter((stat) => visibleStats.includes(stat.key) && (statsData[stat.key] !== -1)).map((stat, index) => {
                        const include = kpiToggle.includes(stat.key)
                        return (
                            <Grid item key={`${index}-${stat.key}`} xs={6} md={4} lg={2} sx={{
                                flex: { xs: '0 0 50%', sm: '0 0 25%', md: 1 },
                                minWidth: 210,
                            }}><BankingWidgetSummary
                                    handleKPI={(key: any) => handleToggleKPI(key)}
                                    spanColor={kpiToggle.includes(stat.key) ? stat.spanColor : theme.palette.background.paper}
                                    kpiToogle={kpiToggle}
                                    title={stat?.title}
                                    val={stat?.key}
                                    valueCompare={compareStatsData[stat.key as keyof typeof compareStatsData] ?? 0}
                                    compareData={((statsData[stat.key as keyof typeof statsData] ?? 0) - (compareStatsData[stat.key as keyof typeof compareStatsData] ?? 0)) / (compareStatsData[stat.key as keyof typeof compareStatsData] ?? 1) * 100}
                                    textColor={pinnedMetrics.includes(stat.key) ? stat?.textColor : theme.palette.text.secondary}
                                    icon={stat?.icon}
                                    color={stat.color}
                                    percent={0}
                                    trendColor={include ? stat?.trendColor : '#e2e5e9'}
                                    total={statsData[stat.key] ?? 0}
                                    chart={{
                                        series: include ? statsData?.time_series_data?.map((item: any) => item[stat.key]) || [] : [],
                                    }}
                                    isFirst={index === 0}
                                    palette={{
                                        lighter: include ? stat["color:lighter"] !== "" ? stat["color:lighter"] ?? stat.color : stat.color : "secondary",
                                        darker: include ? stat["color:darker"] !== "" ? stat["color:darker"] ?? stat.color : stat.color : "secondary"
                                    }}
                                />
                            </Grid>
                        )
                    })
                )}
            </Grid>
            {stats.filter(stat => {
                return !visibleStats.includes(stat.key) && (statsData[stat.key] !== -1);
            }).length > 0 && (
                    <Grid item xs={6} md={4} lg={2} sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        flex: { xs: '0 0 50%', sm: '0 0 25%', md: 0.25 },
                        minWidth: 40,
                        maxWidth: 40,
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'center',
                        height: '100%',
                    }}>
                        {isLoadingKPIs || (!statsData && !statsError) ? (
                            <Skeleton
                                width={'100%'}
                                height={filterStore?.compare ? '170px' : '110px'}
                                style={{
                                    borderRadius: 0,
                                    borderTopRightRadius: 20,
                                }}
                                animation="wave"
                            >
                                <>
                                    <Iconify py="auto" icon="eva:plus-fill" width={24} height={24} />
                                </>
                            </Skeleton>
                        ) :
                            <Button
                                onClick={handleStatsMenuOpen}
                                variant="outlined"
                                sx={{
                                    minWidth: '40px',
                                    width: '40px',
                                    height: '100%',
                                    borderRadius: 0,
                                    borderTopRightRadius: 20,
                                    p: 0,
                                    opacity: 1,
                                    border: "none",
                                    color: theme.palette.text.secondary,
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(33, 43, 54, 0.8)' : 'white',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    ":hover": {
                                        boxShadow: "none",
                                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(33, 43, 54, 0.8)' : 'white',
                                    }
                                }}
                            >
                                <Iconify icon="eva:plus-fill" width={24} height={24} />
                            </Button>
                        }
                        <Menu
                            anchorEl={statsMenuAnchorEl}
                            open={statsMenuOpen}
                            onClose={handleStatsMenuClose}
                            PaperProps={{
                                sx: {
                                    mt: 1.5,
                                    width: 200,
                                    '& .MuiMenuItem-root': {
                                        px: 1,
                                        py: 0.1,
                                        minHeight: 28,
                                        typography: 'body2',
                                        borderRadius: 0.75,
                                    },
                                },
                            }}
                            MenuListProps={{
                                autoFocus: false,
                            }}
                        >
                            <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder={t('dashboard.global.search') || 'Search...'}
                                    value={statsMenuSearch}
                                    onChange={e => setStatsMenuSearch(e.target.value)}
                                    sx={{ mb: 1, height: 40 }}
                                    inputProps={{
                                        style: { fontSize: 15, padding: '10px 12px', height: 40, boxSizing: 'border-box', display: 'flex', alignItems: 'center' },
                                        ref: searchInputRef,
                                        autoFocus: true,
                                    }}
                                    onKeyDown={e => {
                                        // Prevent menu from handling navigation keys
                                        e.stopPropagation();
                                    }}
                                />
                            </Box>
                            {stats
                                .filter(stat => {
                                    // Exclude metrics with no value (but NOT pinnedMetrics)
                                    return statsData[stat.key] !== undefined && statsData[stat.key] !== -1;
                                })
                                .filter(stat =>
                                    !statsMenuSearch ||
                                    stat.title?.toLowerCase().includes(statsMenuSearch.toLowerCase())
                                )
                                .map((stat) => {
                                    const isInVisible = visibleStats.includes(stat.key);
                                    const isInKpi = kpiToggle.includes(stat.key);
                                    return (
                                        <MenuItem
                                            key={stat.key}
                                            onClick={e => {
                                                e.preventDefault();
                                                handleMetricCheckboxClick(stat.key);
                                            }}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                minHeight: 28,
                                                py: 0.25,
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Checkbox
                                                    checked={isInVisible && isInKpi}
                                                    indeterminate={isInVisible && !isInKpi}
                                                    sx={{
                                                        color: isInVisible && !isInKpi ? (theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[400]) : undefined,
                                                        '&.MuiCheckbox-indeterminate': {
                                                            color: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[400],
                                                        },
                                                    }}
                                                />
                                                {stat.title}
                                            </Box>
                                        </MenuItem>
                                    );
                                })}
                        </Menu>
                    </Grid>
                )}
        </>
    )
}